import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import apiClient from "../../../helpers/api-client";

type JSONValue = string | number | boolean | null | undefined | JSONValue[] | { [key: string]: JSONValue };
export type InstituteId = string & { __brand: "InstituteId" };
export type VerityInstituteId = number & { __brand: "VerityInstituteId" };

export enum Product {
	Verity = "verity",
	Candidate = "candidate",
}

export type AccessControlInstitute = {
	id: InstituteId;
	name: string;
	product: string;
	subdomain?: string | null;
	yujaAccountId?: string | null;
	platform?: string | null;
	origin?: string | null;
};

export type VerityInstitute = {
	id: VerityInstituteId;
	accessControlUuid: InstituteId | null;
	clientId: string | null;
	deploymentId: string | null;
	iss: string | null;
	name: string;
	instituteType?: Product;
	platform?: string | null;
	origin?: string | null;
	keyId?: string;
	keyValue?: string;
};

export type VerityInstituteSettings = {
	instituteId: VerityInstituteId;
	liteMode: boolean;
	liteModeViewReports: boolean;
	allowLiveProctoring: boolean;
	allowLivePopIn: boolean;
	allowLivePopInPostReview: boolean;
	allowDemoMode: boolean;
	allowAiStrictMode: boolean;
	allowExtensionPlatform: boolean;
	allowLockdownPlatform: boolean;
	allowMobilePlatform: boolean;
	showAllAssessments: boolean;
};

type Currency = "usd" | "cad" | "eur" | "gbp";

export type VerityInstitutePaymentSettings = {
	instituteId: VerityInstituteId;
	enabled: boolean;
	liveProctoringPrice?: number;
	liveCheckInPrice?: number;
	automatedProctoringPrice?: number;
	currency?: Currency;
};

export type YuJaAccount = {
	yujaAccountId: string;
	salesforceId: string;
	name: string;
};

export type AddInstitutionToYuJaAccountPayload = {
	yujaAccountId: string;
	institutionId: string;
	productType: "verity";
	region: "staging" | "US";
	name: string;
	domain: string | undefined;
};

export type InstituteLogPayload = {
	type: string;
	payload?: Record<string, JSONValue>;
	details?: Record<string, JSONValue>;
	timestamp?: Date;
};

const instituteApi = createApi({
	reducerPath: "institute",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: "/api/",
		})(arg, api, extraOptions),
	tagTypes: [
		"AccessControlInstitute",
		"VerityInstitute",
		"VerityInstituteSettings",
		"VerityInstitutePaymentSettings",
		"YuJaAccount",
	],
	endpoints: (builder) => ({
		getAccessControlInstitute: builder.query<AccessControlInstitute, InstituteId>({
			query: (id) => `access-control/institute/${id}`,
			providesTags: (result) =>
				result ? [{ type: "AccessControlInstitute" as const, id: result.id }] : ["AccessControlInstitute"],
		}),
		getAccessControlInstitutes: builder.query<AccessControlInstitute[], void>({
			query: () => "access-control/institute",
			providesTags: (result) =>
				result
					? [...result.map(({ id }) => ({ type: "AccessControlInstitute" as const, id })), "AccessControlInstitute"]
					: ["AccessControlInstitute"],
			transformResponse: ({ institutes }: { institutes: AccessControlInstitute[] }) => institutes,
		}),
		getVerityInstitutes: builder.query<VerityInstitute[], void>({
			query: () => "verity/lti-institute",
			providesTags: (result) =>
				result
					? [...result.map(({ id }) => ({ type: "VerityInstitute" as const, id })), "VerityInstitute"]
					: ["VerityInstitute"],
		}),
		getVerityInstituteSettings: builder.query<VerityInstituteSettings, VerityInstituteId>({
			query: (id) => `verity/lti-institute/${id}/settings`,
			providesTags: (result) =>
				result ? [{ type: "VerityInstituteSettings" as const, id: result.instituteId }] : ["VerityInstituteSettings"],
		}),
		getVerityInstitutePaymentSettings: builder.query<VerityInstitutePaymentSettings, VerityInstituteId>({
			query: (id) => `verity/lti-institute/${id}/payment-settings`,
			providesTags: (result) =>
				result
					? [{ type: "VerityInstitutePaymentSettings" as const, id: result.instituteId }]
					: ["VerityInstitutePaymentSettings"],
		}),
		getYuJaAccounts: builder.query<YuJaAccount[], void>({
			query: () => "verity/lti-institute/yujaAccounts",
			providesTags: (result) =>
				result
					? [...result.map(({ yujaAccountId }) => ({ type: "YuJaAccount" as const, id: yujaAccountId })), "YuJaAccount"]
					: ["YuJaAccount"],
		}),
		createAccessControlInstitute: builder.mutation<AccessControlInstitute, Omit<AccessControlInstitute, "id">>({
			query: (institute) => ({
				url: "access-control/institute",
				method: "POST",
				body: institute,
			}),
			invalidatesTags: ["AccessControlInstitute"],
		}),
		createVerityInstitute: builder.mutation<VerityInstitute, Omit<VerityInstitute, "id">>({
			query: (institute) => ({
				url: "verity/lti-institute",
				method: "POST",
				body: { ...institute },
			}),
			invalidatesTags: ["VerityInstitute"],
		}),
		updateAccessControlInstitute: builder.mutation<
			AccessControlInstitute,
			Omit<Partial<AccessControlInstitute>, "id"> & Pick<AccessControlInstitute, "id">
		>({
			query: (institute) => ({
				url: `access-control/institute/${institute.id}`,
				method: "PATCH",
				body: { ...institute, id: undefined },
			}),
			invalidatesTags: ["AccessControlInstitute"],
		}),
		updateVerityInstitute: builder.mutation<
			VerityInstitute,
			Omit<Partial<VerityInstitute>, "id"> & Pick<VerityInstitute, "id">
		>({
			query: (institute) => ({
				url: `verity/lti-institute/${institute.id}`,
				method: "PATCH",
				body: { ...institute, id: undefined },
			}),
			invalidatesTags: ["VerityInstitute"],
		}),
		updateVerityInstituteSettings: builder.mutation<VerityInstituteSettings, VerityInstituteSettings>({
			query: (settings) => ({
				url: `verity/lti-institute/${settings.instituteId}/settings`,
				method: "PATCH",
				body: { ...settings, id: undefined },
			}),
			invalidatesTags: (result) =>
				result ? [{ type: "VerityInstituteSettings", id: result?.instituteId }] : ["VerityInstituteSettings"],
		}),
		updateVerityInstitutePaymentSettings: builder.mutation<
			VerityInstitutePaymentSettings,
			VerityInstitutePaymentSettings
		>({
			query: (settings) => ({
				url: `verity/lti-institute/${settings.instituteId}/payment-settings`,
				method: "POST",
				body: { ...settings, id: undefined },
			}),
			invalidatesTags: (result) =>
				result
					? [{ type: "VerityInstitutePaymentSettings", id: result?.instituteId }]
					: ["VerityInstitutePaymentSettings"],
		}),
		updateYuJaAccountWithInstitution: builder.mutation<void, AddInstitutionToYuJaAccountPayload>({
			query: (institutePayload) => ({
				url: "verity/lti-institute/institution",
				method: "POST",
				body: institutePayload,
			}),
			invalidatesTags: ["YuJaAccount"],
		}),
		logInstituteUpdate: builder.mutation<void, InstituteLogPayload>({
			query: (instituteLog) => ({
				url: "verity/debug/institute",
				method: "POST",
				body: {
					details: {
						type: instituteLog.type,
						payload: instituteLog.payload,
						...instituteLog.details,
					},
					timestamp: instituteLog.timestamp,
				},
			}),
		}),
	}),
});

export default instituteApi;
